import { ActionPhase } from '../common/storeUtils.js';
import { EMPLOYEE_SUBSCRIPTION_PAIR_PATH } from '../../components/Employee/Employee.js';
import { TypeKeys } from '../actions/index.js';
import {
  addToActionHistory,
  getPreviousActionState,
  isPreviousActionInProgress,
  updateActionStatePhase,
} from '../common/index.js';
import {
  buildHeaderOnlyItemsQuery,
  getItemsQueryForReports,
  mergeArrays,
  mergeArraysWithIdObject,
  reduceCrudAction,
  reduceDisplayItemsLoadAction,
  reduceSubscriptionUsage,
  reduceSubscriptions,
  validateLoadActionFulfilledResponseCounts,
} from './reducerUtils.js';
import { convertToCommonErrors, getErrorsFromReview } from '../../common/utils/errorUtils.js';
import {
  loadEmployeeCustomerOrdersUseSearchService,
  loadSubscriptionActionsUseSearchService,
} from '../common/elasticsearchUtils.js';
import { sortArray } from '../../common/utils/arrayUtils.js';
import type { ActionsHistory, ItemsQuery } from '../common/store.js';
import type { AuthenticatedUserDataState } from '../../common/types/states.js';
import type {
  Contract,
  CustomerOrder,
  CustomerOrderSearchResponse,
  SubscriptionAction,
  SubscriptionActionSearchResponse,
} from '../../generated/api/models.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { SelfServiceActionTypes } from '../actions/index.js';

export const subscriptionUsageType = {
  LOCAL_USAGE: 'localUsage',
  ROAMING_USAGE: 'roamingUsage',
  TEXT_MESSAGES: 'textMessages',
  VOICE_CALLS: 'voiceCalls',
};

export function authenticatedUserReducer(
  state: (AuthenticatedUserDataState & ActionsHistory) | undefined,
  action: SelfServiceActionTypes
): (AuthenticatedUserDataState & ActionsHistory) | null {
  if (typeof state === 'undefined') {
    return null;
  }
  switch (action.type) {
    case TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS:
      if (isPreviousActionInProgress(action.type, state, 'subscriptionType', action.subscriptionType)) {
        return state;
      } else {
        return {
          ...state,
          actions: addToActionHistory(state, {
            phase: ActionPhase.IN_PROGRESS,
            value: action,
          }),
        };
      }

    case TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptions(action.response, action.subscriptionType, state.subscriptions),
      };
    }

    case TypeKeys.GET_MOBILEID_CONTRACTS_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_MOBILEID_CONTRACTS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        contracts: {
          mobileIdContracts: mergeArrays<Contract>(
            'contractNumber',
            'lastModified',
            state.contracts?.mobileIdContracts,
            action.mobileidContracts
          ),
          loadingContracts: false,
        },
      };
    }

    case TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.CHECK_VALID_EMPLOYEE_SUBSCRIPTION: {
      if (
        state.subscriptions &&
        action.subscriptionToValidate !== undefined &&
        action.subscriptionToValidate !== EMPLOYEE_SUBSCRIPTION_PAIR_PATH &&
        // @ts-ignore
        !state.subscriptions[action.subscriptionType].find(
          (subscription: EmployeeSubscription) => subscription.subscriptionId === action.subscriptionToValidate
        )
      ) {
        return {
          ...state,
          errors: convertToCommonErrors(`subscription ${action.subscriptionToValidate} could not be found`, 404),
        };
      }
      return state;
    }

    case TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE:
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };

    case TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptionUsage(
          subscriptionUsageType.LOCAL_USAGE,
          action.subscriptionId,
          action.response,
          state.subscriptions
        ),
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE:
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };

    case TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptionUsage(
          subscriptionUsageType.ROAMING_USAGE,
          action.subscriptionId,
          action.response,
          state.subscriptions
        ),
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_VOICE_CALLS:
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };

    case TypeKeys.GET_SUBSCRIPTION_VOICE_CALLS_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_VOICE_CALLS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptionUsage(
          subscriptionUsageType.VOICE_CALLS,
          action.subscriptionId,
          action.response,
          state.subscriptions
        ),
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_VOICE_CALLS_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_VOICE_CALLS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_TEXT_MESSAGES:
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };

    case TypeKeys.GET_SUBSCRIPTION_TEXT_MESSAGES_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_TEXT_MESSAGES,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptionUsage(
          subscriptionUsageType.TEXT_MESSAGES,
          action.subscriptionId,
          action.response,
          state.subscriptions
        ),
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_TEXT_MESSAGES_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_TEXT_MESSAGES,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.POLL_FOR_MYSELF_CHANGES: {
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }
    case TypeKeys.POLL_FOR_MYSELF_CHANGES_FAILED: {
      if (state === null) {
        return state;
      }
      const latestChangeRef = action.params?.retainLatestChangeRef ? state.latestChangeRef : undefined;
      return {
        ...state,
        latestChangeRef,
        actions: updateActionStatePhase(
          TypeKeys.POLL_FOR_MYSELF_CHANGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }
    case TypeKeys.STOP_POLL_FOR_MYSELF_CHANGES: {
      if (state === null) {
        return state;
      }
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.POLL_FOR_MYSELF_CHANGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
      };
    }
    case TypeKeys.POLL_FOR_MYSELF_CHANGES_FULFILLED: {
      return {
        ...state,
        latestChangeRef: action.latestChangeRef,
        actions: updateActionStatePhase(
          TypeKeys.POLL_FOR_MYSELF_CHANGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
      };
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING: {
      if (action.validationErrors?.length) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED:
    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.INITIATE_EMPLOYEE_ONBOARDING,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
      };
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.INITIATE_EMPLOYEE_ONBOARDING,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
      };
    }

    case TypeKeys.REVIEW: {
      const errors = getErrorsFromReview(
        action.request,
        action.reviewType,
        action.approve,
        action.rejectReason,
        action.validationErrors
      );
      if (errors) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }

    case TypeKeys.REVIEW_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(TypeKeys.REVIEW, state!, ActionPhase.IN_PROGRESS, ActionPhase.SUCCESS),
      };
    }

    case TypeKeys.REVIEW_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(TypeKeys.REVIEW, state!, ActionPhase.IN_PROGRESS, ActionPhase.FAILED),
      };
    }

    case TypeKeys.RESEND_EMAIL_VERIFICATION_LINK: {
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }

    case TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.RESEND_EMAIL_VERIFICATION_LINK,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
      };
    }

    case TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.RESEND_EMAIL_VERIFICATION_LINK,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.CLEAR_STATE: {
      return {};
    }

    case TypeKeys.CLEAR_EMPLOYEE_SUBS: {
      return {
        ...state,
        subscriptions: undefined,
      };
    }

    case TypeKeys.RESET_ERRORS: {
      return {
        ...state,
        errors: undefined,
      };
    }

    case TypeKeys.LOAD_EMPLOYEE_SUBSCRIPTION_ACTIONS: {
      if (loadSubscriptionActionsUseSearchService(action)) {
        const query: ItemsQuery | undefined = action.reporting
          ? getItemsQueryForReports()
          : buildHeaderOnlyItemsQuery(
              action,
              state.employeeSubscriptionActions,
              state.employeeSubscriptionActions?.total,
              state.employeeSubscriptionActions?.search,
              state.employeeSubscriptionActions?.status
            );

        return {
          ...state,
          actions: query
            ? addToActionHistory(state, {
                phase: ActionPhase.IN_PROGRESS,
                query,
                value: action,
              })
            : state?.actions,
          employeeSubscriptionActions: {
            ...state.employeeSubscriptionActions,
            filter: action.filter,
            search: query?.search,
            sort:
              query?.sort && query?.order
                ? {
                    columnId: query.sort,
                    order: query.order,
                  }
                : state.employeeSubscriptionActions?.sort,
            status: query?.status,
          },
        };
      }
      const getAllItems = !!action.filter || !!action.sort || !!action.search || action.reporting || action.forceLoad;
      const reducedDisplayItem = reduceDisplayItemsLoadAction<
        TypeKeys.LOAD_EMPLOYEE_SUBSCRIPTION_ACTIONS,
        SubscriptionAction
      >(
        action,
        state.employeeSubscriptionActions,
        'subscriptionActionDisplayId',
        getAllItems,
        action.reporting && action.forceLoad ? 0 : undefined,
        action.forceLoad
      );
      return {
        ...state,
        actions: reducedDisplayItem.actions,
        employeeSubscriptionActions: {
          items: action.forceLoad ? undefined : state?.employeeSubscriptionActions?.items,
          total: action.forceLoad ? undefined : state?.employeeSubscriptionActions?.total,
          ...reducedDisplayItem,
        },
      };
    }

    case TypeKeys.LOAD_EMPLOYEE_SUBSCRIPTION_ACTIONS_FULFILLED: {
      const actionStateLoadEmployeeSubs = getPreviousActionState(
        TypeKeys.LOAD_EMPLOYEE_SUBSCRIPTION_ACTIONS,
        state,
        ActionPhase.IN_PROGRESS
      );
      const getSearchResultsForSearchService =
        action.useSearchService && action.query.offset > 0
          ? mergeArraysWithIdObject<SubscriptionActionSearchResponse>(
              'result',
              'subscriptionActionDisplayId',
              'created',
              state.employeeSubscriptionActions!.searchResults,
              action.searchResults
            )
          : action.searchResults;

      return {
        ...state,
        employeeSubscriptionActions: {
          ...state.employeeSubscriptionActions,
          items: sortArray(
            mergeArrays<SubscriptionAction>(
              'subscriptionActionDisplayId',
              'lastModified',
              state.employeeSubscriptionActions?.items,
              action.subscriptionActionResponse
            ),
            state.employeeSubscriptionActions?.sort?.columnId || 'created',
            state.employeeSubscriptionActions?.sort?.order || 'desc'
          ),
          total: action.total,
          actions: updateActionStatePhase(
            TypeKeys.LOAD_EMPLOYEE_SUBSCRIPTION_ACTIONS,
            state!,
            ActionPhase.IN_PROGRESS,
            ActionPhase.SUCCESS
          ),
          errors: action.useSearchService
            ? validateLoadActionFulfilledResponseCounts(
                actionStateLoadEmployeeSubs!.query!,
                action.total,
                action.searchResults?.map(item => item.result),
                state!.errors
              )
            : validateLoadActionFulfilledResponseCounts(
                actionStateLoadEmployeeSubs!.query!,
                action.total,
                action.subscriptionActionResponse,
                state!.errors
              ),
          searchResults: action.useSearchService
            ? getSearchResultsForSearchService
            : state.employeeSubscriptionActions!.searchResults,
        },
      };
    }
    case TypeKeys.LOAD_EMPLOYEE_SUBSCRIPTION_ACTIONS_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.LOAD_EMPLOYEE_SUBSCRIPTION_ACTIONS_FAILED,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.LOAD_EMPLOYEE_CUSTOMER_ORDERS: {
      if (loadEmployeeCustomerOrdersUseSearchService(action)) {
        const query: ItemsQuery | undefined = action.reporting
          ? getItemsQueryForReports()
          : buildHeaderOnlyItemsQuery(
              action,
              state.employeeCustomerOrders,
              state.employeeCustomerOrders?.total,
              state.employeeCustomerOrders?.search,
              state.employeeCustomerOrders?.status
            );

        return {
          ...state,
          actions: query
            ? addToActionHistory(state, {
                phase: ActionPhase.IN_PROGRESS,
                query,
                value: action,
              })
            : state?.actions,
          employeeCustomerOrders: {
            ...state.employeeCustomerOrders,
            filter: action.filter,
            search: query?.search,
            sort:
              query?.sort && query?.order
                ? {
                    columnId: query.sort,
                    order: query.order,
                  }
                : state.employeeCustomerOrders?.sort,
            status: query?.status,
          },
        };
      }

      const getAllItems = !!action.filter || !!action.sort || !!action.search || action.reporting || action.forceLoad;
      const reducedDisplayItem = reduceDisplayItemsLoadAction<TypeKeys.LOAD_EMPLOYEE_CUSTOMER_ORDERS, CustomerOrder>(
        action,
        state.employeeCustomerOrders,
        'customerOrderDisplayId',
        getAllItems,
        action.reporting && action.forceLoad ? 0 : undefined,
        action.forceLoad
      );

      return {
        ...state,
        actions: reducedDisplayItem.actions,
        employeeCustomerOrders: {
          items: action.forceLoad ? undefined : state?.employeeCustomerOrders?.items,
          total: action.forceLoad ? undefined : state?.employeeCustomerOrders?.total,
          ...reducedDisplayItem,
        },
      };
    }

    case TypeKeys.LOAD_EMPLOYEE_CUSTOMER_ORDERS_FULFILLED: {
      const actionStateLoadEmployeeOrders = getPreviousActionState(
        TypeKeys.LOAD_EMPLOYEE_CUSTOMER_ORDERS,
        state!,
        ActionPhase.IN_PROGRESS
      );

      const getSearchResultsForSearchService = action.mergeHeaders
        ? mergeArraysWithIdObject<CustomerOrderSearchResponse>(
            'result',
            'customerOrderDisplayId',
            'lastModified',
            state.employeeCustomerOrders!.searchResults,
            action.searchResults
          )
        : action.searchResults;

      return {
        ...state,
        employeeCustomerOrders: {
          ...state.employeeCustomerOrders,
          items: sortArray(
            mergeArrays<CustomerOrder>(
              'customerOrderId',
              'lastModified',
              state.employeeCustomerOrders?.items,
              action.customerOrders
            ),
            state.employeeCustomerOrders?.sort?.columnId || 'created',
            state.employeeCustomerOrders?.sort?.order || 'desc'
          ),
          total: action.total,
          actions: updateActionStatePhase(
            TypeKeys.LOAD_EMPLOYEE_CUSTOMER_ORDERS,
            state!,
            ActionPhase.IN_PROGRESS,
            ActionPhase.SUCCESS
          ),
          searchResults: action.useSearchService
            ? getSearchResultsForSearchService
            : state.employeeCustomerOrders!.searchResults,
          errors: action.useSearchService
            ? validateLoadActionFulfilledResponseCounts(
                actionStateLoadEmployeeOrders!.query!,
                action.total,
                action.searchResults?.map(item => item.result),
                state?.errors
              )
            : validateLoadActionFulfilledResponseCounts(
                actionStateLoadEmployeeOrders!.query!,
                action.total,
                action.customerOrders,
                state?.errors
              ),
        },
      };
    }

    case TypeKeys.LOAD_EMPLOYEE_CUSTOMER_ORDERS_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.LOAD_EMPLOYEE_CUSTOMER_ORDERS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    default:
      return state;
  }
}
